/**
 * Data hooks for §5 Accounts.
 */
import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as accountsApi from '../../api/accounts.api.js';
import * as masterDataApi from '../../api/masterData.api.js';
import * as usersApi from '../../api/users.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

/** @param {object} params e.g. `{ q, lifecycle_stage, limit, cursor }` */
export function useAccounts(params) {
  return useQuery({
    queryKey: queryKeys.accounts.list(params),
    queryFn: () => accountsApi.listAccounts(params),
    placeholderData: (previous) => previous,
  });
}

/** One account, for the record page. @param {string} id */
export function useAccount(id) {
  return useQuery({
    queryKey: queryKeys.accounts.detail(id),
    queryFn: () => accountsApi.getAccount(id),
    enabled: Boolean(id),
  });
}

/** The parent chain and/or descendant subtree, with roll-ups. */
export function useAccountHierarchy(id, params = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.accounts.hierarchy(id, params),
    queryFn: () => accountsApi.getAccountHierarchy(id, params),
    enabled: Boolean(id) && enabled,
  });
}

/** The people at this company, primary first. */
export function useAccountContacts(id, params = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.accounts.contacts(id, params),
    queryFn: () => accountsApi.getAccountContacts(id, params),
    enabled: Boolean(id) && enabled,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload) => accountsApi.createAccount(payload),
    onSuccess: (account) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
      dispatch(pushToast({ tone: 'success', message: `Account "${account.name}" created.` }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not create that account', detail: error.message }));
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => accountsApi.updateAccount(id, patch, rowVersion),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all }),
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, reason, rowVersion }) => accountsApi.deleteAccount(id, { reason, rowVersion }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
      dispatch(pushToast({ tone: 'success', message: 'Account deleted.' }));
    },
    onError: (error) => {
      const blocked = error?.code === 'account_has_children';
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: blocked
          ? `Still has ${error.payload?.blocking?.join(', ') ?? 'linked records'} attached`
          : stale ? 'Somebody else changed this' : 'Could not delete that account',
        detail: blocked ? 'Move or delete them first.' : stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

export function useRestoreAccount() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (id) => accountsApi.restoreAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
      dispatch(pushToast({ tone: 'success', message: 'Account restored.' }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not restore that account', detail: error.message }));
    },
  });
}

/** Dropdown options the create/edit form needs, fetched once and cached. */
export function useAccountOptions() {
  const ENTITIES = ['customer_types', 'industries', 'countries', 'territories', 'price_lists'];

  const md = useQueries({
    queries: ENTITIES.map((entity) => ({
      queryKey: queryKeys.masterData.options(entity),
      queryFn: () => masterDataApi.listRecords(entity, { limit: 200, is_active: 'true', with_usage: 'false' }),
      staleTime: 300_000,
    })),
  });

  const users = useQuery({
    queryKey: queryKeys.users.list({ limit: 200, picker: true }),
    queryFn: () => usersApi.listUsers({ limit: 200 }),
    staleTime: 300_000,
  });

  const [typeRows, industryRows, countryRows, territoryRows, priceListRows] =
    md.map((q) => q.data?.data ?? []);
  const loading = md.some((q) => q.isLoading) || users.isLoading;
  const userRows = users.data?.data ?? [];

  return useMemo(() => ({
    accountTypes: typeRows.map((r) => ({ value: r.id, label: r.name })),
    industries: industryRows.map((r) => ({ value: r.id, label: r.name })),
    countries: countryRows.map((r) => ({ value: r.id, label: r.name })),
    territories: territoryRows.map((r) => ({ value: r.id, label: r.name })),
    priceLists: priceListRows.map((r) => ({ value: r.id, label: r.name })),
    owners: userRows.map((u) => ({ value: u.id, label: u.full_name ?? u.email })),
    loading,
  }), [typeRows, industryRows, countryRows, territoryRows, priceListRows, userRows, loading]);
}
